/**
 * TicketNext — Universal Support Ticket FAB
 * v3.1.0 — conditional Patient Mobile Number field (modules with
 *          requires_patient_mobile, e.g. Healthcare)
 * v3.0.0 — auto-capture removed (screenshot, browser, page URL, user agent)
 *
 * A floating action button (FAB) that lets logged-in users raise support tickets
 * from any page — Desk (/app/*), CRM (/crm/*), Website, Web Forms, and other SPAs.
 *
 * Architecture:
 *  - Vanilla JS inside an IIFE — zero framework dependencies
 *  - Works in 3 contexts: Desk (jQuery + frappe), Website (frappe.ready), SPA (raw DOM)
 *  - Plain form (subject, description, type/class/module/department) — no auto-capture
 *  - Ticket submitted via frappe.call() on Desk, fetch() fallback on SPAs
 *  - FAB is draggable (mouse + touch) with position saved to localStorage
 *  - Visibility controlled by TicketNext Settings (URL pattern matching)
 *
 * Loading:
 *  - Desk: loaded via app_include_js in hooks.py
 *  - Website/Portal: loaded via web_include_js in hooks.py
 *  - CRM/HRMS: injected into SPA HTML templates by setup.py on bench migrate
 */
(function () {
	'use strict';

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Guard — prevent double-initialization when script is loaded via both
	 * app_include_js and an injected <script> tag (e.g., on Desk + SPA).
	 * ═══════════════════════════════════════════════════════════════════════ */
	if (window.__TN_FAB_V2__) return;
	window.__TN_FAB_V2__ = true;

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Closure State
	 * ═══════════════════════════════════════════════════════════════════════ */
	var _attachedFile  = null;   // File object from the file-upload input
	var _resolvedUser  = '';     // actual user email resolved from API (for SPA contexts)
	var _fabEl         = null;   // reference to the FAB button element (survives SPA DOM wipes)
	var _overlayEl     = null;   // reference to the overlay element (survives SPA DOM wipes)
	var _settings      = {};     // settings from TicketNext Settings (fetched via API)

	// Max upload size (bytes) for the optional file attachment
	var MAX_FILE_SIZE = 10 * 1024 * 1024;  // 10 MB

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Helpers — CSRF, User Detection, Browser
	 * ═══════════════════════════════════════════════════════════════════════ */

	/**
	 * Get the CSRF token for API calls.
	 * Checks multiple locations because Desk, Website, and SPA contexts
	 * store it differently.
	 */
	function getCsrf() {
		if (window.frappe && window.frappe.csrf_token) return window.frappe.csrf_token;
		if (window.csrf_token && window.csrf_token !== 'Guest') return window.csrf_token;
		if (window.boot && window.boot.csrf_token) return window.boot.csrf_token;
		return '';
	}

	/**
	 * Detect the current logged-in user.
	 * Returns the user email on Desk, or '__authenticated__' on SPAs where
	 * we know a session exists (csrf_token present) but don't know the email.
	 * Returns '' for guests — FAB will not render.
	 */
	function getCurrentUser() {
		// Desk: frappe.session is fully populated
		if (window.frappe && window.frappe.session && window.frappe.session.user
			&& window.frappe.session.user !== 'Guest') {
			return window.frappe.session.user;
		}
		// Desk early load: boot object exists before session is set
		if (window.frappe && window.frappe.boot && window.frappe.boot.user) {
			return window.frappe.boot.user.name || '';
		}
		// SPA: some apps set window.user_id, gynoveda_healthcare_spa sets window.user
		if (window.user_id && window.user_id !== 'Guest') {
			return window.user_id;
		}
		if (window.user && typeof window.user === 'string' && window.user !== 'Guest') {
			return window.user;
		}
		// SPA fallback: csrf_token proves user is authenticated
		if (window.csrf_token) {
			return '__authenticated__';
		}
		return '';
	}

	/**
	 * Page path used for module auto-pick — host-agnostic so the same
	 * Page Module Rule patterns work across dev / staging / prod.
	 */
	function getPagePath() {
		try {
			return (window.location.pathname || '/') + (window.location.search || '') + (window.location.hash || '');
		} catch (e) {
			return window.location.href || '';
		}
	}

	/**
	 * Full page URL stored on Ticket.page_url and shown in the modal's
	 * "Page" row. Includes protocol + host so support can click straight
	 * to the page the reporter was on.
	 */
	function getPageHref() {
		try {
			return window.location.href || '';
		} catch (e) {
			return '';
		}
	}

	/**
	 * Extract a human-readable browser name from the user agent string.
	 * Order matters: Edge UA contains "Chrome" + "Safari"; Chrome contains
	 * "Safari" — check most specific first.
	 */
	function getBrowserName() {
		var ua = navigator.userAgent;
		if (ua.indexOf('Edg/') > -1)    return 'Microsoft Edge';
		if (ua.indexOf('Chrome/') > -1)  return 'Google Chrome';
		if (ua.indexOf('Firefox/') > -1) return 'Mozilla Firefox';
		if (ua.indexOf('Safari/') > -1)  return 'Apple Safari';
		return 'Other';
	}

	/**
	 * Format a Date in Indian Standard Time, e.g. "29/05/2026, 15:42:47 IST".
	 * Used for both the modal's Time row and the timestamp stored on the
	 * ticket — so what the reporter sees matches what support reads later,
	 * regardless of the reporter's OS locale.
	 */
	function formatIST(date) {
		try {
			var s = new Intl.DateTimeFormat('en-GB', {
				day: '2-digit', month: '2-digit', year: 'numeric',
				hour: '2-digit', minute: '2-digit', second: '2-digit',
				hour12: false, timeZone: 'Asia/Kolkata',
			}).format(date);
			return s + ' IST';
		} catch (e) {
			return date.toLocaleString();
		}
	}

	/**
	 * Delegate to window.tnPickModuleForPage (defined in page_module_matcher.bundle.js,
	 * loaded ahead of this bundle via app_include_js / web_include_js). The matcher
	 * is shared with Node tests in tests/frontend/. Safe fallback returns '' if the
	 * helper hasn't loaded — which would only happen if hooks.py was edited wrong.
	 */
	function pickModuleForPage(rules, pagePath) {
		if (typeof window !== 'undefined' && typeof window.tnPickModuleForPage === 'function') {
			return window.tnPickModuleForPage(rules, pagePath);
		}
		return '';
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Patient Mobile — conditional field driven by TicketNext Module
	 * ═══════════════════════════════════════════════════════════════════════ */

	/** True when the picked module has `requires_patient_mobile` (e.g. Healthcare). */
	function moduleRequiresMobile(moduleName) {
		var mods = (_settings && _settings.modules) || [];
		for (var i = 0; i < mods.length; i++) {
			if (mods[i].name === moduleName) return !!mods[i].requires_patient_mobile;
		}
		return false;
	}

	/** Strip to bare digits and drop a +91 / leading-0 prefix. Mirrors the backend. */
	function normalizeMobile(raw) {
		var digits = (raw || '').replace(/\D+/g, '');
		if (digits.length === 12 && digits.indexOf('91') === 0) return digits.slice(2);
		if (digits.length === 11 && digits.charAt(0) === '0')   return digits.slice(1);
		return digits;
	}

	/** Show/hide the field for the given module; clears the value when hidden. */
	function togglePatientMobile(moduleName) {
		var wrap  = document.getElementById('sfab-patient-mobile-field');
		var input = document.getElementById('sfab-patient-mobile');
		if (!wrap) return false;
		var required = moduleRequiresMobile(moduleName);
		wrap.style.display = required ? 'block' : 'none';
		if (!required && input) input.value = '';
		return required;
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Toast — lightweight notification bar at top of viewport
	 * ═══════════════════════════════════════════════════════════════════════ */
	function toast(msg, type) {
		var el = document.getElementById('support-fab-toast');
		if (!el) {
			el = document.createElement('div');
			el.id = 'support-fab-toast';
			el.style.cssText = [
				'position:fixed;top:20px;left:50%;transform:translateX(-50%)',
				'padding:12px 20px;border-radius:6px;font-size:14px;font-weight:500',
				'z-index:99999;box-shadow:0 4px 12px rgba(0,0,0,.15);display:none',
				'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif'
			].join(';');
			document.body.appendChild(el);
		}
		el.textContent = msg;
		el.style.background = type === 'error' ? '#f8d7da' : '#d4edda';
		el.style.color      = type === 'error' ? '#721c24' : '#155724';
		el.style.border     = '1px solid ' + (type === 'error' ? '#f5c6cb' : '#c3e6cb');
		el.style.display    = 'block';
		clearTimeout(el._timer);
		el._timer = setTimeout(function () { el.style.display = 'none'; }, 4000);
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * CSS Injection — all styles for FAB, overlay, modal, and form fields
	 * ═══════════════════════════════════════════════════════════════════════ */
	function injectStyles() {
		if (document.getElementById('support-fab-styles')) return;
		var css = [
			/* FAB button — fixed circle (non-draggable) */
			'#support-fab-btn{',
				'position:fixed;bottom:24px;right:24px;',
				'width:52px;height:52px;border-radius:50%;',
				'background:#111;color:#fff;border:none;cursor:pointer;',
				'display:flex;align-items:center;justify-content:center;',
				'box-shadow:0 4px 14px rgba(0,0,0,.35);',
				'z-index:9998;transition:transform .2s,box-shadow .2s;',
				'outline:none;user-select:none;',
			'}',
			'#support-fab-btn:hover{transform:scale(1.1);box-shadow:0 6px 20px rgba(0,0,0,.4);}',

			/* Overlay — fullscreen semi-transparent backdrop */
			'#support-fab-overlay{',
				'position:fixed;inset:0;',
				'background:rgba(0,0,0,.5);',
				'z-index:9999;',
				'display:none;align-items:center;justify-content:center;',
			'}',
			'#support-fab-overlay.open{display:flex;}',

			/* Modal — centered card */
			'#support-fab-modal{',
				'background:#fff;border-radius:10px;padding:24px;',
				'width:480px;max-width:92vw;max-height:90vh;overflow-y:auto;',
				'box-shadow:0 8px 32px rgba(0,0,0,.2);',
				'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;',
				'font-size:14px;color:#1a1a1a;',
			'}',
			'#support-fab-modal h3{margin:0 0 18px;font-size:18px;font-weight:600;color:#111;}',

			/* Form fields */
			'.sfab-field{margin-bottom:14px;}',
			'.sfab-field label{display:block;margin-bottom:5px;font-weight:500;color:#444;font-size:13px;}',
			'.sfab-field select,.sfab-field input,.sfab-field textarea{',
				'width:100%;padding:8px 10px;border:1px solid #ddd;border-radius:6px;',
				'font-size:14px;box-sizing:border-box;outline:none;transition:border-color .15s;',
			'}',
			'.sfab-field select:focus,.sfab-field input:focus,.sfab-field textarea:focus{border-color:#666;}',
			'.sfab-field textarea{resize:vertical;min-height:80px;}',

			/* Metadata panel — user */
			'.sfab-meta-box{',
				'display:none;',  // hidden from UI — metadata still sent to backend on submit
				'background:#f8f9fa;border:1px solid #e9ecef;border-radius:6px;',
				'padding:10px 12px;margin-bottom:14px;',
			'}',
			'.sfab-meta-row{',
				'display:flex;align-items:flex-start;gap:6px;',
				'font-size:12px;color:#555;margin-bottom:4px;',
			'}',
			'.sfab-meta-row:last-child{margin-bottom:0;}',
			'.sfab-meta-label{font-weight:600;color:#333;min-width:46px;flex-shrink:0;}',
			'.sfab-meta-value{color:#555;word-break:break-all;}',
			'.sfab-meta-url{color:#1a73e8;font-family:monospace;font-size:11px;}',

			/* Clipboard paste zone — click then Cmd+V / Ctrl+V */
			'#sfab-paste-zone{',
				'border:2px dashed #ccc;border-radius:6px;',
				'padding:14px;text-align:center;',
				'font-size:13px;color:#777;background:#fafafa;',
				'cursor:text;transition:border-color .15s, background .15s, color .15s;',
				'user-select:none;',
			'}',
			'#sfab-paste-zone:focus{outline:none;border-color:#1a73e8;background:#f0f7ff;color:#1a73e8;}',
			'#sfab-paste-zone.has-image{border-color:#28a745;background:#f0fff4;color:#1f7a34;}',
			'#sfab-paste-zone .sfab-paste-thumb{max-height:90px;max-width:100%;border-radius:4px;margin-top:8px;display:block;margin-left:auto;margin-right:auto;}',

			/* Action buttons */
			'.sfab-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:20px;}',
			'.sfab-btn{padding:9px 18px;border:none;border-radius:6px;cursor:pointer;',
				'font-size:14px;font-weight:500;transition:opacity .15s;}',
			'.sfab-btn:disabled{opacity:.55;cursor:not-allowed;}',
			'.sfab-btn-cancel{background:#f0f0f0;color:#333;}',
			'.sfab-btn-cancel:hover:not(:disabled){background:#e0e0e0;}',
			'.sfab-btn-submit{background:#111;color:#fff;}',
			'.sfab-btn-submit:hover:not(:disabled){background:#333;}',

			/* Small inline buttons (Remove / Replace screenshot) */
			'.sfab-btn-small{',
				'padding:4px 10px;border:1px solid #ddd;border-radius:4px;',
				'font-size:12px;background:#f8f8f8;color:#555;cursor:pointer;',
				'display:inline-block;transition:background .15s;',
			'}',
			'.sfab-btn-small:hover{background:#eee;}',

			/* File upload input */
			'#sfab-file{',
				'font-size:13px;padding:6px;border:1px solid #ddd;border-radius:6px;',
				'width:100%;box-sizing:border-box;cursor:pointer;',
			'}',

			/* Capturing indicator — visible pill while screenshot is in progress */
			'#support-fab-capturing{',
				'position:fixed;top:24px;left:50%;transform:translateX(-50%);',
				'background:rgba(17,17,17,.92);color:#fff;',
				'padding:10px 18px;border-radius:999px;',
				'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;',
				'font-size:13px;font-weight:500;letter-spacing:.2px;',
				'display:none;align-items:center;gap:10px;',
				'box-shadow:0 6px 24px rgba(0,0,0,.28),0 2px 6px rgba(0,0,0,.18);',
				'z-index:100000;pointer-events:none;',
				'animation:sfab-cap-in .18s ease-out;',
			'}',
			'#support-fab-capturing.open{display:inline-flex;}',
			'#support-fab-capturing .sfab-cap-spinner{',
				'width:14px;height:14px;border-radius:50%;',
				'border:2px solid rgba(255,255,255,.25);border-top-color:#fff;',
				'animation:sfab-cap-spin .8s linear infinite;',
			'}',
			'@keyframes sfab-cap-spin{to{transform:rotate(360deg);}}',
			'@keyframes sfab-cap-in{from{opacity:0;transform:translate(-50%,-8px);}to{opacity:1;transform:translate(-50%,0);}}',
		].join('');
		var tag = document.createElement('style');
		tag.id = 'support-fab-styles';
		tag.textContent = css;
		document.head.appendChild(tag);
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * DOM Injection — creates the FAB button, overlay, modal, and form
	 * ═══════════════════════════════════════════════════════════════════════ */
	function injectDOM() {
		// ── FAB Button (ticket icon, fixed position, draggable) ──────────
		var fab = document.createElement('button');
		fab.id = 'support-fab-btn';
		fab.title = 'Raise a Support Ticket';
		fab.setAttribute('aria-label', 'Raise a Support Ticket');
		fab.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></svg>';

		// Apply admin-configured color from TicketNext Settings (fab_color).
		// CSS sets a default; only override when admin picked something non-empty.
		if (_settings && _settings.fab_color) {
			fab.style.background = _settings.fab_color;
		}

		// Apply admin-configured position from TicketNext Settings (fab_position).
		// The FAB is fixed (non-draggable) — its position comes solely from this
		// setting; there is no per-user localStorage drag override.
		if (_settings && _settings.fab_position === 'Bottom Left') {
			fab.style.left  = '24px';
			fab.style.right = 'auto';
		}

		_fabEl = fab;  // store reference for SPA DOM persistence
		document.body.appendChild(fab);

		// FAB is fixed in place (non-draggable) by design — anchored to the
		// admin-configured corner via CSS + fab_position above. Drag handlers
		// and the localStorage position persistence were removed intentionally.

		// ── Overlay + Modal ─────────────────────────────────────────────
		var overlay = document.createElement('div');
		overlay.id = 'support-fab-overlay';

		var modal = document.createElement('div');
		modal.id = 'support-fab-modal';
		modal.innerHTML = [
			// ── Form View (visible by default) ──
			'<div id="sfab-form-view">',
				'<h3>Raise a Ticket</h3>',

				// Module selector (populated from TicketNext Module records on open)
				'<div class="sfab-field">',
					'<label for="sfab-module">Module <span style="color:#c00">*</span></label>',
					'<select id="sfab-module">',
						'<option value="">Select a module...</option>',
					'</select>',
				'</div>',

				// Patient Mobile — rendered only for modules flagged
				// requires_patient_mobile (Healthcare). Hidden + cleared otherwise.
				'<div class="sfab-field" id="sfab-patient-mobile-field" style="display:none;">',
					'<label for="sfab-patient-mobile">Patient Mobile Number <span style="color:#c00">*</span></label>',
					'<input id="sfab-patient-mobile" type="tel" inputmode="numeric" autocomplete="off" placeholder="10-digit mobile number" maxlength="15" />',
				'</div>',

				// Department selector — BRD §5.2.2 routing dimension
				// (Accounts / Tech / SCM / Data / IT / HR → group inbox per BRD §5.2.3)
				// Visible but optional per Abhay 2026-05-28: reporter picks if relevant,
				// no module → default_department auto-fill (their choice should win from
				// the start). Backend accepts empty department; auto-assign falls back
				// to ticket type default → settings default.
				'<div class="sfab-field">',
					'<label for="sfab-department">Assigned To</label>',
					'<select id="sfab-department">',
						'<option value="">Select assigned to...</option>',
					'</select>',
				'</div>',

				// Issue Type selector — populated dynamically from
				// TicketNext Ticket Type docs (admins manage at
				// /app/ticketnext-ticket-type), same pattern as Module / Department.
				'<div class="sfab-field">',
					'<label for="sfab-type">Subject <span style="color:#c00">*</span></label>',
					'<select id="sfab-type">',
						'<option value="">Select a subject...</option>',
					'</select>',
				'</div>',

				// Title (required)
				'<div class="sfab-field">',
					'<label for="sfab-title">Title <span style="color:#c00">*</span></label>',
					'<input id="sfab-title" type="text" placeholder="Brief summary of the issue..." maxlength="140" />',
				'</div>',

				// Description (required)
				'<div class="sfab-field">',
					'<label for="sfab-desc">Description <span style="color:#c00">*</span></label>',
					'<textarea id="sfab-desc" placeholder="What were you trying to do? What went wrong?"></textarea>',
				'</div>',

				// File attachment (optional) — pick a file OR paste a screenshot below
				'<div class="sfab-field">',
					'<label for="sfab-file">Attach File <span style="color:#888;font-weight:400;">(optional — PDF, Excel, CSV, image, etc.)</span></label>',
					'<input type="file" id="sfab-file" accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt,.zip" />',
					'<div id="sfab-file-name" style="font-size:12px;color:#555;margin-top:4px;"></div>',
				'</div>',

				// Clipboard paste zone — click then Cmd+V (Mac) / Ctrl+V (Windows)
				'<div class="sfab-field">',
					'<label>Or paste a screenshot</label>',
					'<div id="sfab-paste-zone" tabindex="0" role="textbox" aria-label="Paste screenshot here">',
						'<span id="sfab-paste-text">Click here, then press <b>Cmd+V</b> (Mac) or <b>Ctrl+V</b> (Windows) to paste an image from your clipboard.</span>',
					'</div>',
				'</div>',

				// Metadata panel — auto-populated, read-only context info
				'<div class="sfab-meta-box">',
					'<div class="sfab-meta-row">',
						'<span class="sfab-meta-label">User</span>',
						'<span id="sfab-meta-user" class="sfab-meta-value"></span>',
					'</div>',
					'<div class="sfab-meta-row">',
						'<span class="sfab-meta-label">Page</span>',
						'<span id="sfab-meta-page" class="sfab-meta-value sfab-meta-url"></span>',
					'</div>',
					'<div class="sfab-meta-row">',
						'<span class="sfab-meta-label">Browser</span>',
						'<span id="sfab-meta-browser" class="sfab-meta-value"></span>',
					'</div>',
					'<div class="sfab-meta-row">',
						'<span class="sfab-meta-label">Time</span>',
						'<span id="sfab-meta-ts" class="sfab-meta-value"></span>',
					'</div>',
				'</div>',

				// Form action buttons
				'<div class="sfab-actions">',
					'<button class="sfab-btn sfab-btn-cancel" id="sfab-cancel">Cancel</button>',
					'<button class="sfab-btn sfab-btn-submit" id="sfab-submit">Submit Ticket</button>',
				'</div>',
			'</div>',

			// ── Success View (shown after submission) ──
			'<div id="sfab-success-view" style="display:none; text-align:center;">',
				'<h3 style="color:#28a745; margin-bottom:20px;">&#10003; Ticket Submitted</h3>',
				'<p style="font-size:16px; margin-bottom:10px;">Your ticket has been created successfully.</p>',
				'<div style="background:#f0f8ff; border:1px solid #b0d4ff; border-radius:6px; padding:16px; margin-bottom:20px;">',
					'<div style="font-size:12px; color:#666; margin-bottom:6px;">Ticket ID</div>',
					'<div id="sfab-success-ticket-id" style="font-size:20px; font-weight:bold; color:#111; font-family:monospace;"></div>',
				'</div>',
				'<p style="font-size:13px; color:#666; margin-bottom:20px;">',
					'A confirmation email has been sent to you. Our support team will review your ticket shortly.',
				'</p>',
				'<div class="sfab-actions">',
					'<button class="sfab-btn sfab-btn-cancel" id="sfab-success-close">Close</button>',
					'<button class="sfab-btn sfab-btn-submit" id="sfab-success-view-btn">View Ticket</button>',
				'</div>',
			'</div>',
		].join('');

		// Stop all clicks inside the modal from leaking to host app
		modal.addEventListener('click', function (e) { e.stopPropagation(); });

		overlay.appendChild(modal);
		_overlayEl = overlay;  // store reference for SPA DOM persistence
		document.body.appendChild(overlay);

		// ── FAB Click → open modal ──────────────────────────────────────
		fab.addEventListener('click', function (e) {
			// Stop propagation so host-app modals (CRM, HRMS) don't close
			e.stopPropagation();
			e.preventDefault();

			// Populate auto-captured metadata
			var userEl    = document.getElementById('sfab-meta-user');
			var pageEl    = document.getElementById('sfab-meta-page');
			var browserEl = document.getElementById('sfab-meta-browser');
			var tsEl      = document.getElementById('sfab-meta-ts');
			if (userEl)    userEl.textContent    = _resolvedUser || getCurrentUser();
			if (pageEl)    pageEl.textContent    = getPageHref();
			if (browserEl) browserEl.textContent = getBrowserName();
			if (tsEl)      tsEl.textContent      = formatIST(new Date());

			// Populate Module + Department + Issue Type dropdowns from settings
			// (idempotent — clear and re-fill on every open so admin changes
			// to the source doctypes show up without a page reload).
			function _fillSelect(el, items, placeholder) {
				if (!el) return;
				el.innerHTML = '<option value="">' + placeholder + '</option>';
				for (var i = 0; i < items.length; i++) {
					var opt = document.createElement('option');
					opt.value = items[i].name;
					opt.textContent = items[i].label || items[i].name;
					el.appendChild(opt);
				}
			}
			var _moduleSelEl   = document.getElementById('sfab-module');
			var _deptSelEl     = document.getElementById('sfab-department');
			var _typeSelEl     = document.getElementById('sfab-type');
			_fillSelect(_moduleSelEl,
				(_settings && _settings.modules) || [],
				'Select a module...');
			_fillSelect(_deptSelEl,
				(_settings && _settings.departments) || [],
				'Select assigned to...');
			_fillSelect(_typeSelEl,
				(_settings && _settings.ticket_types) || [],
				'Select a subject...');

			// Subject (issue type) options are scoped to the chosen department.
			// _settings.dept_subjects maps a department -> allowed ticket type names.
			// A department absent from the map (or with an empty list) imposes no
			// restriction, so every subject is offered. With no department picked we
			// also show all subjects (the initial _fillSelect above already did).
			function _subjectsForDept(deptName) {
				var all = (_settings && _settings.ticket_types) || [];
				var map = (_settings && _settings.dept_subjects) || {};
				var allowed = deptName && map[deptName];
				if (!allowed || !allowed.length) return all;
				return all.filter(function (t) { return allowed.indexOf(t.name) !== -1; });
			}
			function _refillSubjects(deptName) {
				var prev = _typeSelEl ? _typeSelEl.value : '';
				_fillSelect(_typeSelEl, _subjectsForDept(deptName), 'Select a subject...');
				// Preserve the prior subject only if it's still valid for this department.
				if (_typeSelEl && prev) {
					var stillValid = false;
					for (var _s = 0; _s < _typeSelEl.options.length; _s++) {
						if (_typeSelEl.options[_s].value === prev) { stillValid = true; break; }
					}
					_typeSelEl.value = stillValid ? prev : '';
				}
			}
			// Idempotent: onchange assignment replaces, so re-opening never stacks handlers.
			if (_deptSelEl) {
				_deptSelEl.onchange = function () { _refillSubjects(_deptSelEl.value); };
			}

			// Module → Patient Mobile visibility. Assignment (not addEventListener)
			// so re-opening the modal never stacks handlers. Also fires for the
			// synthetic change dispatched by the auto-pick block below.
			if (_moduleSelEl) {
				_moduleSelEl.onchange = function () { togglePatientMobile(_moduleSelEl.value); };
			}

			// Module → Department auto-fill intentionally disabled (Abhay 2026-05-28).
			// Department is optional and the reporter picks it themselves; no default
			// should pre-select Accounts (or anything else) on Module change.

			// Reset form fields
			document.getElementById('sfab-title').value = '';
			document.getElementById('sfab-desc').value  = '';
			document.getElementById('sfab-file').value = '';
			document.getElementById('sfab-file-name').textContent = '';
			var _pasteZoneReset = document.getElementById('sfab-paste-zone');
			var _pasteTextReset = document.getElementById('sfab-paste-text');
			if (_pasteZoneReset) _pasteZoneReset.classList.remove('has-image');
			if (_pasteTextReset) {
				_pasteTextReset.innerHTML = 'Click here, then press <b>Cmd+V</b> (Mac) or <b>Ctrl+V</b> (Windows) to paste an image from your clipboard.';
			}
			var _moduleReset = document.getElementById('sfab-module');
			if (_moduleReset) _moduleReset.value = '';
			var _deptReset = document.getElementById('sfab-department');
			if (_deptReset) _deptReset.value = '';
			var _typeReset = document.getElementById('sfab-type');
			if (_typeReset) _typeReset.value = '';
			var _mobileReset = document.getElementById('sfab-patient-mobile');
			if (_mobileReset) _mobileReset.value = '';
			togglePatientMobile('');

			// Auto-pick Module from URL via TicketNext Page Module Rule.
			// Runs after the reset block so we set the value on a clean form.
			// Dispatches a synthetic change for any downstream listeners.
			// Skipped silently when no rule matches.
			try {
				var _autoPick = pickModuleForPage(
					(_settings && _settings.page_module_rules) || [],
					getPagePath()
				);
				if (_autoPick && _moduleReset) {
					for (var _i = 0; _i < _moduleReset.options.length; _i++) {
						if (_moduleReset.options[_i].value === _autoPick) {
							_moduleReset.value = _autoPick;
							_moduleReset.dispatchEvent(new Event('change'));
							break;
						}
					}
				}
			} catch (e) { /* never block modal open on rule-match failure */ }

			_attachedFile = null;

			// Show form view (in case success view was shown from a previous submission)
			showFormView();

			overlay.classList.add('open');
			setTimeout(function () { document.getElementById('sfab-title').focus(); }, 50);
		});

		// ── Close modal: Cancel button, click outside, or Escape key ────
		document.getElementById('sfab-cancel').addEventListener('click', function () {
			overlay.classList.remove('open');
		});
		overlay.addEventListener('click', function (e) {
			e.stopPropagation(); // prevent host-app modals from closing
			if (e.target === overlay) overlay.classList.remove('open');
		});
		document.addEventListener('keydown', function (e) {
			if (e.key === 'Escape' && overlay.classList.contains('open')) {
				overlay.classList.remove('open');
			}
		});

		// ── Submit Ticket ────────────────────────────────────────────────
		document.getElementById('sfab-submit').addEventListener('click', function () {
			var btn    = document.getElementById('sfab-submit');
			var title  = (document.getElementById('sfab-title').value || '').trim();
			var desc   = (document.getElementById('sfab-desc').value  || '').trim();
			var type   = document.getElementById('sfab-type').value;
			var modEl  = document.getElementById('sfab-module');
			var mod    = modEl ? modEl.value : '';
			var deptEl = document.getElementById('sfab-department');
			var dept   = deptEl ? deptEl.value : '';
			var mobEl  = document.getElementById('sfab-patient-mobile');
			var mobile = normalizeMobile(mobEl ? mobEl.value : '');

			// Validate required fields — order matches form layout so the
			// first empty field is what we focus. Department is no longer
			// required (hidden in the form); it auto-fills from the module's
			// default_department and is sent with the payload when present.
			if (!mod)   { if (modEl)  modEl.focus();  toast('Please select a module.', 'error'); return; }
			if (moduleRequiresMobile(mod) && mobile.length !== 10) {
				if (mobEl) mobEl.focus();
				toast('Please enter a valid 10-digit patient mobile number.', 'error');
				return;
			}
			if (!type)  { document.getElementById('sfab-type').focus();  toast('Please select a subject.', 'error'); return; }
			if (!title) { document.getElementById('sfab-title').focus(); toast('Please enter a title.', 'error'); return; }
			if (!desc)  { document.getElementById('sfab-desc').focus();  toast('Please enter a description.', 'error'); return; }

			btn.disabled    = true;
			btn.textContent = 'Submitting...';

			function resetBtn() {
				btn.disabled    = false;
				btn.textContent = 'Submit Ticket';
			}

			/**
			 * Build the payload and send to backend.
			 * Called directly (no file) or after FileReader completes (with file).
			 */
			function doSubmit(fileData, fileName) {
				var payload = {
					title:       title,
					description: desc,
					issue_type:  type,
					module:      mod  || null,
					department:  dept || null,
					patient_mobile: mobile || null,
					url:         getPageHref(),
					user_agent:  navigator.userAgent,
					timestamp:   formatIST(new Date()),
					file_base64: fileData || null,
					file_name:   fileName || null,
				};
				submitPayload(payload, resetBtn);
			}

			// If a file is attached, read it as base64 first
			if (_attachedFile) {
				// Enforce file size limit
				if (_attachedFile.size > MAX_FILE_SIZE) {
					toast('File too large. Maximum size is 10 MB.', 'error');
					resetBtn();
					return;
				}
				var reader = new FileReader();
				reader.onload = function (ev) {
					doSubmit(ev.target.result, _attachedFile.name);
				};
				reader.onerror = function () {
					doSubmit(null, null); // submit without file on read error
				};
				reader.readAsDataURL(_attachedFile);
			} else {
				doSubmit(null, null);
			}
		});

		/**
		 * Send the ticket payload to the backend.
		 * Uses frappe.call() on Desk (handles CSRF automatically),
		 * falls back to fetch() on SPA pages where frappe.call is unavailable.
		 */
		function submitPayload(payload, resetBtn) {
			if (window.frappe && window.frappe.call) {
				// Desk context — use frappe.call for automatic error handling
				window.frappe.call({
					method: 'ticketnext.support_utils.create_support_ticket',
					args:   payload,
					callback: function (r) {
						resetBtn();
						if (r && r.message && r.message.status === 'success') {
							var ticketId = r.message.ticket_name || '';
							toast('Ticket ' + ticketId + ' raised successfully!', 'success');
							showSuccessView(ticketId);
						} else {
							toast('Submission failed. Please try again.', 'error');
						}
					},
					error: function () {
						resetBtn();
						toast('Failed to submit. Please try again.', 'error');
					}
				});
			} else {
				// SPA context — use fetch() with manual CSRF header
				fetch('/api/method/ticketnext.support_utils.create_support_ticket', {
					method:      'POST',
					credentials: 'same-origin',
					headers: {
						'Content-Type':        'application/json',
						'Accept':              'application/json',
						'X-Frappe-CSRF-Token': getCsrf(),
						'X-Requested-With':    'XMLHttpRequest'
					},
					body: JSON.stringify(payload)
				})
				.then(function (res) { return res.json(); })
				.then(function (data) {
					if (data.message && data.message.status === 'success') {
						var ticketId = data.message.ticket_name || '';
						toast('Ticket ' + ticketId + ' raised successfully!', 'success');
						showSuccessView(ticketId);
					} else {
						throw new Error('Submission failed');
					}
				})
				.catch(function () {
					toast('Failed to submit. Please try again.', 'error');
				})
				.finally(function () {
					resetBtn();
				});
			}
		}
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Ticket URL — build a link to the ticket in Desk
	 * ═══════════════════════════════════════════════════════════════════════ */
	function getTicketUrl(ticketId) {
		var base = window.frappe && window.frappe.utils && typeof window.frappe.utils.get_url === 'function'
			? window.frappe.utils.get_url()
			: window.location.origin;
		return base + '/app/ticketnext-ticket/' + ticketId;
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * URL Pattern Matching — controls FAB visibility per TicketNext Settings
	 *
	 * Patterns use * as a wildcard:
	 *   /crm/*            → matches /crm/leads, /crm/deals/view/list, etc.
	 *   /app/sales-order/* → matches /app/sales-order/SO-0001, etc.
	 * ═══════════════════════════════════════════════════════════════════════ */
	function matchesPattern(url, pattern) {
		// Escape regex special chars, then convert * to .*
		var escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
		escaped = escaped.replace(/\*/g, '.*');
		return new RegExp('^' + escaped + '$').test(url);
	}

	/**
	 * Decide whether the FAB should appear on the current page.
	 * Reads visibility mode and URL patterns from settings fetched via API.
	 */
	function shouldShowFab(settings) {
		var path     = window.location.pathname;
		var mode     = settings.visibility || 'Show Everywhere';
		var patterns = settings.url_patterns || [];

		if (mode === 'Show Everywhere') return true;

		var matched = patterns.some(function (p) {
			return p && matchesPattern(path, p.trim());
		});

		if (mode === 'Only on Listed URLs') return matched;
		if (mode === 'Hide on Listed URLs') return !matched;
		return true;
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Initialization
	 *
	 * 1. Check if user is logged in
	 * 2. Fetch visibility settings from TicketNext Settings via API
	 * 3. Check URL patterns to decide if FAB should show
	 * 4. Inject DOM (with 1500ms delay on SPA apps to let Vue/React mount first)
	 * ═══════════════════════════════════════════════════════════════════════ */
	function init() {
		// Already injected (e.g., SPA client-side navigation re-triggered init)
		if (document.getElementById('support-fab-btn')) return;

		var u = getCurrentUser();
		if (!u || u === 'Guest') return;

		fetchSettings(function (settings) {
			_settings = settings;  // store globally for FAB click handler
			if (!shouldShowFab(settings)) return;

			// Resolve the actual user email from API response (for SPA contexts
			// where getCurrentUser() returns '__authenticated__')
			if (settings.user && (u === '__authenticated__' || !u.includes('@'))) {
				_resolvedUser = settings.user;
			} else {
				_resolvedUser = u;
			}

			if (!window.frappe || !window.frappe.ui) {
				// SPA context: delay injection so Vue/React has time to mount.
				// Without this, the SPA framework may overwrite our DOM elements.
				setTimeout(function () { bootstrapFab(u); }, 1500);
			} else {
				// Desk context: frappe UI is ready, inject immediately
				bootstrapFab(u);
			}
		});
	}

	/**
	 * Final step: inject all DOM and attach handlers.
	 * On SPA pages, also starts a MutationObserver to survive DOM wipes.
	 */
	function bootstrapFab(u) {
		if (document.getElementById('support-fab-btn')) return; // double-check after delay
		injectStyles();
		injectDOM();
		setupFormHandlers();
		setupSuccessHandlers();
		setupDomPersistence();
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * SPA DOM Persistence
	 *
	 * SPA frameworks (Vue in CRM, React in HRMS) replace document.body children
	 * on route changes, which removes our FAB and overlay elements. We use a
	 * MutationObserver on document.body to detect when our elements are removed
	 * and immediately re-attach them. The stored closure references (_fabEl,
	 * _overlayEl) survive because they live in JS memory, not in the DOM tree.
	 * ═══════════════════════════════════════════════════════════════════════ */
	function setupDomPersistence() {
		// Only needed on SPA pages (no frappe.ui means not on Desk)
		if (window.frappe && window.frappe.ui) return;

		var observer = new MutationObserver(function (mutations) {
			// Check if our elements were removed from the DOM
			var fabMissing     = _fabEl     && !document.body.contains(_fabEl);
			var overlayMissing = _overlayEl && !document.body.contains(_overlayEl);

			if (fabMissing || overlayMissing) {
				// Temporarily disconnect to avoid triggering ourselves
				observer.disconnect();

				if (fabMissing)     document.body.appendChild(_fabEl);
				if (overlayMissing) document.body.appendChild(_overlayEl);

				// Re-observe after re-attaching
				observer.observe(document.body, { childList: true });
			}
		});

		observer.observe(document.body, { childList: true });
	}

	/**
	 * Fetch FAB visibility settings from TicketNext Settings via XHR.
	 * Falls back to empty settings (show everywhere) on any error.
	 *
	 * The XHR has a hard 5s timeout — a hung server call would otherwise
	 * block the FAB from ever appearing on the page (audit fab-js.md P5).
	 * On timeout/error/HTTP error, we run the callback with `{}` so the
	 * FAB still renders with safe defaults. Idempotent: `_settled` guards
	 * against onload + ontimeout double-firing in edge browsers.
	 */
	function fetchSettings(callback) {
		var csrf = getCsrf();
		var xhr = new XMLHttpRequest();
		var settled = false;
		function settle(payload) {
			if (settled) return;
			settled = true;
			callback(payload);
		}
		xhr.open('POST', '/api/method/ticketnext.api.get_fab_settings');
		xhr.setRequestHeader('Content-Type', 'application/json');
		xhr.setRequestHeader('Accept', 'application/json');
		if (csrf) xhr.setRequestHeader('X-Frappe-CSRF-Token', csrf);
		xhr.timeout = 5000;
		xhr.onload = function () {
			if (xhr.status === 200) {
				try {
					var resp = JSON.parse(xhr.responseText);
					settle(resp.message || {});
				} catch (e) {
					settle({}); // malformed JSON — use defaults
				}
			} else {
				settle({}); // API error — show FAB with defaults
			}
		};
		xhr.onerror   = function () { settle({}); };
		xhr.ontimeout = function () { settle({}); };
		xhr.send(JSON.stringify({}));
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Form Handlers — file attachment
	 * ═══════════════════════════════════════════════════════════════════════ */
	function setupFormHandlers() {
		// Additional file attachment — track selected file and show name
		document.getElementById('sfab-file').addEventListener('change', function (e) {
			var file   = e.target.files && e.target.files[0];
			var nameEl = document.getElementById('sfab-file-name');
			if (file) {
				if (file.size > MAX_FILE_SIZE) {
					toast('File too large. Maximum size is 10 MB.', 'error');
					e.target.value = '';
					_attachedFile = null;
					nameEl.textContent = '';
					return;
				}
				_attachedFile = file;
				var sizeKB = (file.size / 1024).toFixed(1);
				nameEl.textContent = file.name + ' (' + sizeKB + ' KB)';
			} else {
				_attachedFile = null;
				nameEl.textContent = '';
			}
		});

		// Clipboard paste — Cmd+V / Ctrl+V an image while the modal is open.
		// Whichever was set last (file picker or paste) wins; single _attachedFile.
		document.addEventListener('paste', function (e) {
			var overlay = document.getElementById('support-fab-overlay');
			if (!overlay || !overlay.classList.contains('open')) return;

			var items = (e.clipboardData && e.clipboardData.items) || [];
			for (var i = 0; i < items.length; i++) {
				var it = items[i];
				if (it.type && it.type.indexOf('image/') === 0) {
					e.preventDefault();
					var blob = it.getAsFile();
					if (!blob) return;

					// Pasted clipboard images come with a generic "image.png"
					// name — rename for clarity in the file listing.
					var ts  = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
					var ext = (blob.type.split('/')[1] || 'png').toLowerCase();
					var named;
					try {
						named = new File([blob], 'pasted_' + ts + '.' + ext, { type: blob.type });
					} catch (_e) {
						// Older browsers without File constructor — fall back to blob
						named = blob;
						named.name = 'pasted_' + ts + '.' + ext;
					}

					if (named.size > MAX_FILE_SIZE) {
						toast('Pasted image too large. Maximum size is 10 MB.', 'error');
						return;
					}

					_attachedFile = named;

					var nameEl = document.getElementById('sfab-file-name');
					var sizeKB = (named.size / 1024).toFixed(1);
					if (nameEl) nameEl.textContent = named.name + ' (' + sizeKB + ' KB)';

					var zoneEl = document.getElementById('sfab-paste-zone');
					var textEl = document.getElementById('sfab-paste-text');
					if (zoneEl) zoneEl.classList.add('has-image');
					if (textEl) {
						var reader = new FileReader();
						reader.onload = function (ev) {
							textEl.innerHTML =
								'Pasted: <b>' + named.name + '</b> (' + sizeKB + ' KB)' +
								'<img class="sfab-paste-thumb" src="' + ev.target.result + '" alt="Pasted screenshot preview"/>';
						};
						reader.readAsDataURL(named);
					}
					return;
				}
			}
		});
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Success View Handlers — shown after ticket is created
	 * ═══════════════════════════════════════════════════════════════════════ */
	function setupSuccessHandlers() {
		// Close button — hide overlay and reset to form view
		document.getElementById('sfab-success-close').addEventListener('click', function () {
			document.getElementById('support-fab-overlay').classList.remove('open');
			showFormView();
		});

		// View Ticket button — open ticket in Desk in a new tab
		document.getElementById('sfab-success-view-btn').addEventListener('click', function () {
			var ticketId = document.getElementById('sfab-success-ticket-id').textContent;
			if (ticketId) {
				window.open(getTicketUrl(ticketId), '_blank');
			}
		});
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * View Toggle — switch between form and success views within the modal
	 * ═══════════════════════════════════════════════════════════════════════ */
	function showFormView() {
		document.getElementById('sfab-form-view').style.display    = 'block';
		document.getElementById('sfab-success-view').style.display = 'none';
	}

	function showSuccessView(ticketId) {
		document.getElementById('sfab-form-view').style.display    = 'none';
		document.getElementById('sfab-success-view').style.display = 'block';
		document.getElementById('sfab-success-ticket-id').textContent = ticketId;
	}

	/* ═══════════════════════════════════════════════════════════════════════════
	 * Bootstrap — detect context and schedule init()
	 *
	 * Three execution paths:
	 *  1. Desk: jQuery + frappe available → $(document).ready(init)
	 *  2. Website/Portal: frappe available without jQuery → frappe.ready(init)
	 *  3. SPA (CRM/HRMS): no frappe → DOMContentLoaded or immediate
	 * ═══════════════════════════════════════════════════════════════════════ */
	if (window.$ && window.frappe) {
		$(document).ready(init);
	} else if (window.frappe && typeof window.frappe.ready === 'function') {
		frappe.ready(init);
	} else {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', init);
		} else {
			init();
		}
	}
})();
